import { useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import type { FocusEvent } from 'react';
import { useCallback, useEffect, useMemo } from 'react';
import type { SubmitHandler, UseFormSetValue } from 'react-hook-form';

import { RequestQueryKeysE } from 'declarations/enums/react-query';
import type { NoopT } from 'declarations/types/common';
import type { SelectOptionT } from 'declarations/types/select';
import { getErrorMessage, getValidationErrorMessage } from 'helpers/apiError';
import { useFetchClaimStatuses } from 'hooks/api/useClaimStatuses';
import { useFetchTicketPriorities } from 'hooks/api/useTicketPriorities';
import { useFetchTicketRequestCategories } from 'hooks/api/useTicketRequestCategories';
import { useChangeTicketDetails } from 'hooks/api/useTickets';
import {
  useFetchTicketRequestSubcategories,
  useFetchTicketRequestSubcategoriesByCategoryId,
} from 'hooks/api/useTicketSubcategories';

import useTicketDetailsPopup from './useTicketDetailsPopup';

export type ChangeTicketDetailsFormValuesT = {
  ticketId: string;
  amount: string;
  claimStatusId: number;
  claimSubmittedDate: Date | null;
  advisorNumber: string;
  techNumber: string;
  ticketPriorityId: number;
  subCategoryId: number | null;
  dmsScheduleAccountNumber: string;
};

type UseChangeTicketPriorityPopupT = {
  ticketPrioritiesOptions: SelectOptionT[];
  claimStatusesOptions: SelectOptionT[];
  ticketSubcategoriesOptions: SelectOptionT[];
  /** True when ticket has categoryName but no matching category in categories list (show no results in dropdown) */
  hasRequestSubcategoryNoMatch: boolean;
  isLoadingTicketPrioritiesOptions: boolean;
  isLoadingClaimStatusesOptions: boolean;
  isLoadingTicketSubcategoriesOptions: boolean;
  isChangingTicketDetails: boolean;
  isTicketDetailsChanged: boolean;
  isTicketDetailsChangeError: boolean;
  changeTicketDetailsErrorMessage: string | undefined;
  changeTicketDetailsValidationErrorMessage: string | undefined;
  showPopupContent: boolean;
  handleAmountBlur: (
    setValue: UseFormSetValue<ChangeTicketDetailsFormValuesT>
  ) => (e: FocusEvent<HTMLInputElement>) => void;
  handleChangeTicketDetails: SubmitHandler<ChangeTicketDetailsFormValuesT>;
  handleClosePopup: (onClosePopupCb: NoopT) => NoopT;
};

const useChangeTicketDetailsForm = (): UseChangeTicketPriorityPopupT => {
  /**
   * Hooks
   */
  const queryClient = useQueryClient();
  const { ticketDetails, selectedTicketId } = useTicketDetailsPopup();

  const hasTicketDetailsWithRoofTop = !!ticketDetails && !!ticketDetails.roofTop?.id;
  const roofTopId = ticketDetails?.roofTop?.id ?? '';

  const claimStatusesRequestParams = {
    isEnabled: hasTicketDetailsWithRoofTop,
    roofTopId: roofTopId,
  };

  // Fetch categories when ticket details are loaded (for resolving categoryId by name if needed)
  const ticketRequestCategoriesResult = useFetchTicketRequestCategories(
    hasTicketDetailsWithRoofTop,
    ticketDetails?.roofTop?.id
  );

  // Category id for subcategories: from ticket details (if API returns it) or resolve by category name from categories response
  const selectedCategoryId = useMemo(() => {
    if (ticketDetails?.requestCategoryId != null) {
      return ticketDetails.requestCategoryId;
    }
    if (!ticketDetails?.categoryName || !ticketRequestCategoriesResult.data?.length) {
      return undefined;
    }
    const normalize = (s: string): string => s.trim().toLowerCase().replace(/\s+/g, ' ');
    const normalizedTicketCategory = normalize(ticketDetails.categoryName);
    const category = ticketRequestCategoriesResult.data.find(
      cat => normalize(cat.categoryName) === normalizedTicketCategory
    );
    return category?.id;
  }, [
    ticketDetails?.requestCategoryId,
    ticketDetails?.categoryName,
    ticketRequestCategoriesResult.data,
  ]);

  // Determine if this is a schedule ticket (has subCategoryId) or regular ticket (has requestSubCategoryId)
  const isScheduleTicket = useMemo(() => {
    return (
      ticketDetails?.categoryName?.toLowerCase() === 'schedule' || !!ticketDetails?.subCategoryId
    );
  }, [ticketDetails?.categoryName, ticketDetails?.subCategoryId]);

  // Fetch old subcategories API for schedule tickets
  const oldTicketSubcategoriesResult = useFetchTicketRequestSubcategories({
    isEnabled: hasTicketDetailsWithRoofTop && isScheduleTicket,
    roofTopId: ticketDetails?.roofTop?.id,
  });

  // Fetch subcategories for regular tickets - use roofTop + categoryId (from ticket or resolved by category name)
  const ticketSubcategoriesResult = useFetchTicketRequestSubcategoriesByCategoryId({
    isEnabled: hasTicketDetailsWithRoofTop && selectedCategoryId != null && !isScheduleTicket,
    categoryId: selectedCategoryId,
    roofTopId: ticketDetails?.roofTop?.id,
  });

  // Refetch subcategories each time the details popup is open with a ticket so we always have fresh data
  useEffect(() => {
    if (!selectedTicketId || !ticketDetails?.roofTop?.id) return;
    if (isScheduleTicket) {
      oldTicketSubcategoriesResult.refetch();
    } else if (selectedCategoryId != null) {
      ticketSubcategoriesResult.refetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refetch when popup opens (selectedTicketId) and ticket is loaded
  }, [
    selectedTicketId,
    ticketDetails?.id,
    ticketDetails?.roofTop?.id,
    isScheduleTicket,
    selectedCategoryId,
  ]);

  /**
   * Api hooks
   */
  const ticketPrioritiesResult = useFetchTicketPriorities(true);

  const claimStatusesResult = useFetchClaimStatuses(claimStatusesRequestParams);

  const changeTicketDetailsMutation = useChangeTicketDetails({
    onSuccess: () => {
      queryClient.invalidateQueries([RequestQueryKeysE.FetchTicketMessages]);
      queryClient.invalidateQueries([RequestQueryKeysE.FetchTicket]);
      queryClient.invalidateQueries([RequestQueryKeysE.FetchTickets]);
      queryClient.invalidateQueries([RequestQueryKeysE.FetchTicketsCountByRoofTop]);
    },
  });

  /**
   * Variables
   */
  const ticketPrioritiesOptions = useMemo(
    () =>
      ticketPrioritiesResult.data
        ? ticketPrioritiesResult.data?.map(({ id, name }) => ({
            label: name,
            value: id,
          }))
        : [],
    [ticketPrioritiesResult.data]
  );

  // Use old API options for schedule tickets, new API options for regular tickets
  const ticketSubcategoriesOptions = useMemo(() => {
    if (isScheduleTicket && oldTicketSubcategoriesResult.data) {
      return oldTicketSubcategoriesResult.data.map(({ id, name }) => ({
        label: name,
        value: id,
      }));
    }
    if (!isScheduleTicket && ticketSubcategoriesResult.data) {
      return ticketSubcategoriesResult.data.map(({ id, name }) => ({
        label: name,
        value: id,
      }));
    }
    return [];
  }, [isScheduleTicket, oldTicketSubcategoriesResult.data, ticketSubcategoriesResult.data]);

  const changeTicketDetailsErrorMessage = useMemo(
    () => getErrorMessage(changeTicketDetailsMutation.error),
    [changeTicketDetailsMutation.error]
  );

  const changeTicketDetailsValidationErrorMessage = useMemo(
    () => getValidationErrorMessage(changeTicketDetailsMutation.error),
    [changeTicketDetailsMutation.error]
  );

  const claimStatusesOptions = useMemo(
    () =>
      claimStatusesResult.data
        ? claimStatusesResult.data?.map(({ id, name }) => ({
            label: name,
            value: id,
          }))
        : [],
    [claimStatusesResult.data]
  );

  /**
   * Handlers
   */
  const resetChangeTicketDetailsMutationState = useCallback(() => {
    if (changeTicketDetailsMutation.isSuccess || changeTicketDetailsMutation.isError) {
      changeTicketDetailsMutation.reset();
    }
  }, [changeTicketDetailsMutation]);

  const handleClosePopup = useCallback(
    (onClosePopupCb: NoopT) => () => {
      onClosePopupCb();
      resetChangeTicketDetailsMutationState();
    },
    [resetChangeTicketDetailsMutationState]
  );

  const handleChangeTicketDetails = useCallback(
    (data: ChangeTicketDetailsFormValuesT) => {
      const isScheduleTicket = ticketDetails?.categoryName?.toLowerCase() === 'schedule';

      // Get subcategory name if subcategory is selected
      let requestSubCategoryName: string | null = null;

      if (data.subCategoryId) {
        let subcategoryNameValue: string | null = null;

        if (isScheduleTicket && oldTicketSubcategoriesResult.data) {
          const selectedSubcategory = oldTicketSubcategoriesResult.data.find(
            sub => sub.id === data.subCategoryId
          );
          subcategoryNameValue = selectedSubcategory?.name || null;
        } else if (!isScheduleTicket && ticketSubcategoriesResult.data) {
          const selectedSubcategory = ticketSubcategoriesResult.data.find(
            sub => sub.id === data.subCategoryId
          );
          subcategoryNameValue = selectedSubcategory?.name || null;
          requestSubCategoryName = subcategoryNameValue;
        }
      }

      changeTicketDetailsMutation.mutate({
        ...data,
        ticketRoofTopId: ticketDetails?.roofTop?.id ?? '',
        claimSubmittedDate: data.claimSubmittedDate
          ? format(data.claimSubmittedDate, 'yyyy-MM-dd')
          : null,
        subCategoryId: isScheduleTicket && data.subCategoryId ? data.subCategoryId : null,
        requestSubCategoryId: !isScheduleTicket && data.subCategoryId ? data.subCategoryId : null,
        requestSubCategoryName: requestSubCategoryName,
        dmsScheduleAccountNumber: ticketDetails?.dmsScheduleAccountNumber ?? null,
      });
    },
    [
      changeTicketDetailsMutation,
      ticketDetails?.categoryName,
      ticketDetails?.dmsScheduleAccountNumber,
      ticketDetails?.roofTop?.id,
      ticketSubcategoriesResult.data,
      oldTicketSubcategoriesResult.data,
    ]
  );

  const setAmountValue = (
    value: string,
    setValue: UseFormSetValue<ChangeTicketDetailsFormValuesT>
  ): void => {
    setValue('amount', value, { shouldValidate: true });
  };

  const handleAmountBlur = useCallback(
    (setValue: UseFormSetValue<ChangeTicketDetailsFormValuesT>) =>
      (e: FocusEvent<HTMLInputElement>) => {
        const { value } = e.target;
        const [numbersBeforeDot, numbersAfterDot] = value.split('.');
        const hasOneDecimal = !!numbersAfterDot && numbersAfterDot.length === 1;
        const hasTwoDecimal = !!numbersAfterDot && numbersAfterDot.length >= 2;

        if (!value) {
          setAmountValue('0.00', setValue);
        } else if (value && hasOneDecimal) {
          setAmountValue(`${numbersBeforeDot}.${numbersAfterDot}0`, setValue);
        } else if (value && !hasTwoDecimal) {
          setAmountValue(`${numbersBeforeDot}.00`, setValue);
        }
      },
    []
  );

  // Ticket has categoryName but no category in the list matched by name -> show no results in Request Subcategory dropdown
  const hasRequestSubcategoryNoMatch = useMemo(() => {
    if (isScheduleTicket) return false;
    if (!ticketDetails?.categoryName) return false;
    if (ticketRequestCategoriesResult.isLoading || !ticketRequestCategoriesResult.data)
      return false;
    return selectedCategoryId == null;
  }, [
    isScheduleTicket,
    ticketDetails?.categoryName,
    ticketRequestCategoriesResult.isLoading,
    ticketRequestCategoriesResult.data,
    selectedCategoryId,
  ]);

  // Only show subcategory loader while fetching and no result yet; when API returns empty, show "No subcategories" not loader
  const isLoadingTicketSubcategoriesOptions = useMemo(() => {
    if (isScheduleTicket) {
      return (
        oldTicketSubcategoriesResult.isLoading && oldTicketSubcategoriesResult.data === undefined
      );
    }
    return ticketSubcategoriesResult.isLoading && ticketSubcategoriesResult.data === undefined;
  }, [
    isScheduleTicket,
    oldTicketSubcategoriesResult.isLoading,
    oldTicketSubcategoriesResult.data,
    ticketSubcategoriesResult.isLoading,
    ticketSubcategoriesResult.data,
  ]);

  return {
    isLoadingTicketPrioritiesOptions: ticketPrioritiesResult.isLoading,
    isLoadingClaimStatusesOptions: claimStatusesResult.isLoading,
    isLoadingTicketSubcategoriesOptions,
    hasRequestSubcategoryNoMatch,
    ticketPrioritiesOptions,
    claimStatusesOptions,
    ticketSubcategoriesOptions,
    isChangingTicketDetails: changeTicketDetailsMutation.isLoading,
    isTicketDetailsChanged: changeTicketDetailsMutation.isSuccess,
    isTicketDetailsChangeError: changeTicketDetailsMutation.isError,
    changeTicketDetailsErrorMessage,
    changeTicketDetailsValidationErrorMessage,
    showPopupContent: changeTicketDetailsMutation.isIdle || changeTicketDetailsMutation.isLoading,
    handleChangeTicketDetails,
    handleClosePopup,
    handleAmountBlur,
  };
};

export default useChangeTicketDetailsForm;
